export interface ImagingAttachment {
  type: 'pdf' | 'image' | 'text' | 'html' | 'binary' | 'link';
  title: string;
  data: string;
  contentType: string;
}

/**
 * Open an imaging attachment in a new tab: links directly, text and HTML as
 * a rendered page, and binaries (PDF, image) decoded from base64 into a blob.
 */
export function openAttachmentInNewTab(attachment: ImagingAttachment, fallbackTitle: string): void {
  if (attachment.type === 'link') {
    window.open(attachment.data, '_blank');
    return;
  }

  let blob: Blob;

  if (attachment.type === 'html' || attachment.type === 'text' ||
      attachment.contentType?.includes('text/html') || attachment.contentType?.includes('text/plain')) {
    // Text content may or may not be base64 encoded.
    let htmlContent = attachment.data;
    try {
      htmlContent = atob(attachment.data);
    } catch {
      htmlContent = attachment.data;
    }

    if (attachment.contentType?.includes('text/html') && !htmlContent.includes('<html')) {
      htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${attachment.title || fallbackTitle}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
            }
            h1, h2, h3 { color: #333; }
            p { margin: 10px 0; }
            .impression {
              background-color: #f0f8ff;
              padding: 10px;
              border-left: 3px solid #0066cc;
              margin: 20px 0;
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `;
    }

    blob = new Blob([htmlContent], { type: attachment.contentType });
  } else {
    try {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      blob = new Blob([new Uint8Array(byteNumbers)], { type: attachment.contentType });
    } catch (e) {
      console.error('Error decoding attachment:', e);
      blob = new Blob([attachment.data], { type: 'text/plain' });
    }
  }

  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
